"use client";

import PinterestIcon from "@/components/icons/PinterestIcon";
import XingIcon from "@/components/icons/XingIcon";
import { Header, TitleH2 } from "@/components/shared/public";
import ScrollReveal from "@/components/shared/public/ScrollReveal";
import { Link } from "@/i18n/navigation";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { JSX } from "react";
import { hamburgKinder } from "../../../../../public/images/partner";

const SocialIcon = ({
  icon,
  href,
  label,
}: {
  icon: JSX.Element;
  href: string;
  label: string;
}) => (
  <Link
    aria-label={label}
    className="lg:hover:border-primary lg:hover:text-primary text-ring border-input flex transform cursor-pointer flex-col items-center justify-center rounded-full border-2 p-2 transition-all duration-300"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    {icon}
  </Link>
);

export default function SocialEngagementContent() {
  const t = useTranslations("socialEngagement");
  const tC = useTranslations("common");

  return (
    <>
      <Header
        title={t("head.title")}
        subtitle={t("head.lead")}
        breadcrumb={[
          { label: tC("breadcrumbs.home"), href: "/" },
          { label: t("head.page") },
        ]}
        containerClass="gs--page-column gs--page-column--narrow"
      />

      <div className="gs--page-column gs--page-column--narrow flex flex-col py-12 sm:py-16">
        <section className="gs--partner-section">
          <TitleH2 title={t("intro.title")} />
          <p className="gs--partner-section__text text-ring">
            {t.rich("intro.text", {
              br: () => <br />,
              bold: (children) => <strong>{children}</strong>,
            })}
          </p>
        </section>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("hamburgMachtKinderGesund.title")} />
            <div>
              <Image
                src={hamburgKinder}
                alt={t("hamburgMachtKinderGesund.imageAlt")}
                className="float-left mr-4 mb-2 w-40 sm:w-48"
              />
              <p className="gs--partner-section__text text-ring">
                {t.rich("hamburgMachtKinderGesund.text", {
                  br: () => <br />,
                  bold: (children) => <strong>{children}</strong>,
                })}
              </p>
            </div>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("knowledgeSharing.title")} />
            <p className="gs--partner-section__text text-ring">
              {t.rich("knowledgeSharing.text", {
                br: () => <br />,
                bold: (children) => <strong>{children}</strong>,
              })}
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section">
            <TitleH2 title={t("team.title")} />
            <p className="gs--partner-section__text text-ring">
              {t.rich("team.text", {
                br: () => <br />,
                bold: (children) => <strong>{children}</strong>,
              })}
            </p>
          </section>
        </ScrollReveal>

        <ScrollReveal>
          <section className="gs--partner-section gs--partner-section--last">
            <TitleH2 title={t("social.title")} />
            <p className="gs--partner-section__text text-ring">
              {t("social.text")}
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-start gap-3">
              <SocialIcon
                href="https://www.facebook.com/GrundrissSchmiede/"
                icon={<Facebook size={20} />}
                label="Facebook"
              />
              <SocialIcon
                href="https://www.instagram.com/grundrissschmiede/"
                icon={<Instagram size={20} />}
                label="Instagram"
              />
              <SocialIcon
                href="https://de.pinterest.com/grundriss_schmiede/"
                icon={<PinterestIcon width={20} height={20} />}
                label="Pinterest"
              />
              <SocialIcon
                href="https://www.linkedin.com/company/grundriss-schmiede/"
                icon={<Linkedin size={20} />}
                label="LinkedIn"
              />
              <SocialIcon
                href="https://www.xing.com/pages/immomates-gmbh"
                icon={<XingIcon width={20} height={20} />}
                label="Xing"
              />
              <SocialIcon
                href="https://www.youtube.com/channel/UCKZX84eqTlPrAQz5A6KDFWw"
                icon={<Youtube size={20} />}
                label="YouTube"
              />
            </div>
          </section>
        </ScrollReveal>
      </div>
    </>
  );
}
